/**
 * Formato de pesos y porcentajes del módulo, en formato mexicano (coma de
 * miles, punto decimal), igual que el tablero de indicadores.
 *
 * Las metas son SIN IVA (como `cmeta` y la venta neta del tablero); el
 * formato no lo dice, lo dice la etiqueta de la columna.
 */

const vacio = (n: number | null | undefined): n is null | undefined =>
  n === null || n === undefined || Number.isNaN(n);

/** `105125` → «$105,125»; `1234.5` → «$1,234.5». Hasta 2 decimales, sólo si los hay. */
export function pesos(n: number | null | undefined): string {
  if (vacio(n)) return '—';
  const s = Math.abs(n).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  return `${n < 0 ? '−' : ''}$${s}`;
}

/** Pesos sin centavos, para totales y la tabla de autorización. */
export function pesosEnteros(n: number | null | undefined): string {
  if (vacio(n)) return '—';
  const s = Math.round(Math.abs(n)).toLocaleString('es-MX');
  return `${Math.round(n) < 0 ? '−' : ''}$${s}`;
}

/** Diferencia con signo: «+$1,200» / «−$800» / «$0». */
export function pesosConSigno(n: number | null | undefined): string {
  if (vacio(n)) return '—';
  const r = Math.round(n);
  if (r === 0) return '$0';
  return `${r > 0 ? '+' : '−'}$${Math.abs(r).toLocaleString('es-MX')}`;
}

/** Fracción con signo: `0.032` → «+3.2 %». */
export function pctConSigno(f: number | null | undefined, dec = 1): string {
  if (vacio(f)) return '—';
  const v = f * 100;
  // Lo que se redondea a cero se escribe sin signo: «−0.0 %» parece un error.
  if (Math.abs(v) < 0.5 / 10 ** dec) return `${(0).toFixed(dec)} %`;
  const s = Math.abs(v).toLocaleString('es-MX', {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  });
  return `${v > 0 ? '+' : '−'}${s} %`;
}

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/**
 * Un INSTANTE de la API (`enviadoEn`, `resueltoEn`: marca de tiempo completa)
 * en hora de México: «26 sep 2026, 14:05».
 *
 * Se arma con `formatToParts` y meses propios en vez de `toLocaleString`, cuyo
 * texto cambia con la versión de ICU del navegador («sep» / «sept»).
 */
export function instanteMx(v: string | null | undefined): string {
  if (!v) return '—';
  const t = Date.parse(v);
  if (!Number.isFinite(t)) return '—';
  try {
    const p = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Mexico_City',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date(t));
    const x = (tipo: string) => p.find((q) => q.type === tipo)?.value ?? '';
    const hora = x('hour') === '24' ? '00' : x('hour');
    return `${Number(x('day'))} ${MESES[Number(x('month')) - 1]} ${x('year')}, ${hora}:${x('minute')}`;
  } catch {
    return new Date(t).toISOString().slice(0, 16).replace('T', ' ');
  }
}

/** Palabras que van en minúscula dentro de un nombre («María de la Luz»). */
const MINUSCULAS = new Set(['de', 'del', 'la', 'las', 'los', 'y', 'e', 'da', 'van', 'von']);

/**
 * `ESTHER MORALES` → «Esther Morales»; `MARÍA DE LA LUZ` → «María de la Luz».
 *
 * El catálogo trae a los supervisores en MAYÚSCULAS porque así son llave de
 * acceso (`metas_alcance_usuario`): aquí sólo se cambia cómo se LEEN, nunca se
 * usa el resultado para comparar.
 */
export function nombrePropio(v: string | null | undefined): string {
  const s = (v ?? '').trim();
  if (!s) return '';
  return s
    .toLocaleLowerCase('es-MX')
    .split(/(\s+|-)/)
    .map((p, i) =>
      /^\s+$|^-$/.test(p) || (i > 0 && MINUSCULAS.has(p)) ? p : p.charAt(0).toLocaleUpperCase('es-MX') + p.slice(1),
    )
    .join('');
}

/** El día (`AAAA-MM-DD`) y la hora (`HH:MM`) de un instante en hora de México. */
function partesMx(t: number): { dia: string; hora: string } | null {
  try {
    const p = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Mexico_City',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date(t));
    const x = (tipo: string) => p.find((q) => q.type === tipo)?.value ?? '';
    const hora = x('hour') === '24' ? '00' : x('hour');
    return { dia: `${x('year')}-${x('month')}-${x('day')}`, hora: `${hora}:${x('minute')}` };
  } catch {
    return null;
  }
}

/**
 * Un instante de la API dicho como en el diseño, en hora de México:
 * «hoy 10:42», «ayer 18:05» o «26 sep, 14:05». `ahora` se inyecta en las pruebas.
 */
export function momentoMx(v: string | null | undefined, ahora: number = Date.now()): string {
  if (!v) return '';
  const t = Date.parse(v);
  if (!Number.isFinite(t)) return '';
  const p = partesMx(t);
  const hoy = partesMx(ahora);
  const ayer = partesMx(ahora - 86_400_000);
  if (!p || !hoy || !ayer) return instanteMx(v);
  if (p.dia === hoy.dia) return `hoy ${p.hora}`;
  if (p.dia === ayer.dia) return `ayer ${p.hora}`;
  const [, mes, dia] = p.dia.split('-');
  return `${Number(dia)} ${MESES[Number(mes) - 1]}, ${p.hora}`;
}
